import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { isEqual } from 'lodash';
import { Box, Tabs, Group, Loader, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { fetchProposal, getLawyerList } from '../../services/proposalsRoutes';
import { v4 as uuidv4 } from 'uuid';
import PostProposalGeneration from './PostProposalGeneration/PostProposalGeneration';
import { Center } from '@mantine/core';
import PreProposalGeneration from './PreProposalGeneration/PreProposalGeneration';
import { Proposal } from '@/interfaces/types';
import { ProposalProvider, useProposal } from './context/ProposalContext';
import {
  initialProposalState,
  introLetterEng,
  introLetterPol,
  PROPOSALACTIONS,
} from './reducers/proposalReducer';
import { USERFEEDBACKACTIONS } from './reducers/userFeedbackReducer';
// import {
//   convertOldFees,
//   isOldFeeProposal,
// } from './PostProposalGeneration/SecondTab/convertOldFees';
import {
  defaultFeeTextEn,
  defaultFeeTextPl,
} from './PostProposalGeneration/SecondTab/defaultFeeText';
import FirstTab from './PostProposalGeneration/FirstTab/FirstTab';
import SecondTab from './PostProposalGeneration/SecondTab/SecondTab';
import ThirdTab from './PostProposalGeneration/ThirdTab/ThirdTab';

function EditProposalContent() {
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const { proposal, dispatchProposal, dispatchOtherData, userFeedback, dispatchUserFeedback } =
    useProposal();
  const [proposalDataLoading, setProposalDataLoading] = useState(true);
  const { loadingScope, loadingAssumptions, loadingLawyers, loadingExperiences } = userFeedback;
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = searchParams.get('tab') || 'first-section';
  const [activeTab, setActiveTab] = useState<string | null>(initial);
  const { generating } = userFeedback;
  // Support testing via URL query: /proposals/edit-proposal?proposalId=<uuid>
  const state = (location.state || {}) as { proposalId?: string; isNew?: boolean };
  const proposalId = state.proposalId || searchParams.get('proposalId') || '';
  const isNew = state.isNew;

  // Check if proposal has been generated
  const isProposalGenerated = checkProposalGenerated(proposal, initialProposalState);

  // Fetch Proposal Data
  useEffect(() => {
    async function initOrFetch() {
      try {
        if (isNew) {
          // Do not fetch when creating a new proposal
          dispatchProposal({
            type: PROPOSALACTIONS.INITIALIZE,
            payload: {
              ...initialProposalState,
              id: proposalId,
              proposalDate: new Date(),
            },
          });
          return;
        }

        const accessToken = await getAccessTokenSilently();
        const fetchedData = await fetchProposal(accessToken, proposalId);

        // Ensure hourly rate fees rows have IDs
        if (fetchedData && fetchedData.fees && fetchedData.fees.HourlyRate) {
          for (const rate of fetchedData.fees.HourlyRate) {
            if (!rate.id) {
              rate.id = uuidv4();
            }
          }
        }
        if (fetchedData) {
          dispatchProposal({ type: PROPOSALACTIONS.INITIALIZE, payload: fetchedData });
        }
      } catch (error: any) {
        console.error('Error fetching proposal:', error);
        const status = error?.status || error?.response?.status;
        const message: string = (error?.message || '').toString();
        // If the proposal is missing, return to the list
        if (status === 404 || message.includes('404') || message.toLowerCase().includes('not found')) {
          notifications.show({
            title: t('proposalNotFoundTitle'),
            message: t('proposalNotFoundMsg'),
            color: 'red',
            autoClose: 2500,
            position: 'top-right',
          });
          navigate('/proposals', { replace: true });
        } else {
          alert('Failed to fetch proposal data.');
        }
      } finally {
        setProposalDataLoading(false);
      }
    }

    initOrFetch();
  }, [getAccessTokenSilently, proposalId, dispatchProposal, isNew]);

  // Fetch Lawyer List on mount
  useEffect(() => {
    const fetchLawyerList = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await getLawyerList(accessToken);
        dispatchOtherData({ field: 'allLawyers', value: response });
      } catch (error) {
        console.error('Error fetching lawyer list:', error);
        alert('Failed to fetch lawyer list.');
      }
    };

    fetchLawyerList();
  }, [getAccessTokenSilently, dispatchOtherData]); 

  useEffect(() => {
    if (!loadingScope && !loadingAssumptions && !loadingLawyers && !loadingExperiences) {
      dispatchUserFeedback({
        type: USERFEEDBACKACTIONS.SET_FIELD,
        field: 'generating',
        value: false,
      });
    }
  }, [loadingScope, loadingAssumptions, loadingLawyers, loadingExperiences, dispatchUserFeedback]);

  const handleTabChange = (val: string | null) => {
    setActiveTab(val);

    const next = new URLSearchParams(location.search);
    val ? next.set('tab', val) : next.delete('tab');

    navigate(
      {
        pathname: location.pathname,
        search: next.toString(),
        hash: location.hash,
      },
      {
        replace: true,
        state: location.state,
      }
    );
  };

  if (proposalDataLoading) {
    return (
      <Center h="80vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Box mx="5%" w="90%">
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab value="first-section">{t('firstSection')}</Tabs.Tab>
          <Tabs.Tab 
            value="second-section"
            disabled={!isProposalGenerated}
            style={{
              color: isProposalGenerated ? undefined : 'var(--mantine-color-gray-5)',
              cursor: isProposalGenerated ? 'pointer' : 'not-allowed',
            }}
          >
            {t('secondSection')}
          </Tabs.Tab>
          <Tabs.Tab 
            value="third-section"
            disabled={!isProposalGenerated}
            style={{
              color: isProposalGenerated ? undefined : 'var(--mantine-color-gray-5)',
              cursor: isProposalGenerated ? 'pointer' : 'not-allowed',
            }}
          >
            {t('thirdSection')}
          </Tabs.Tab>
          {generating && (
            <Group ml="auto" pr="xs" mb="xs">
              <Loader size="sm" />
              <Text>Generating...</Text>
            </Group>
          )}
        </Tabs.List>

        <Tabs.Panel value="first-section" pt="md">
          {!isProposalGenerated ? <PreProposalGeneration /> : <FirstTab />}
        </Tabs.Panel>

        <Tabs.Panel value="second-section" pt="md">
          <SecondTab />
        </Tabs.Panel>

        <Tabs.Panel value="third-section" pt="md">
          <ThirdTab />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

export default function EditProposalPage() {
  return (
    <ProposalProvider>
      <EditProposalContent />
    </ProposalProvider>
  );
}

function checkProposalGenerated(proposal: Proposal, defaultProposal: Proposal) {
  const fieldsToCompare = ['scope', 'assumptions', 'lawyers', 'experience', 'specialRoles'];
  for (const field of fieldsToCompare) {
    if (!isEqual(proposal[field as keyof Proposal], defaultProposal[field as keyof Proposal])) {
      return true;
    }
  }

  // intro needs to be handled separately
  if (proposal.intro !== introLetterEng && proposal.intro !== introLetterPol) {
    return true;
  }

  if (
    proposal.fees.length > 0 &&
    isEqual(proposal.fees, defaultFeeTextEn) &&
    isEqual(proposal.fees, defaultFeeTextPl)
  ) {
    return true;
  }
  return false;
}

// For checking for array equality:
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((val, i) => val === b[i]);
}
